import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import React, { FC, useMemo } from 'react'

import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { PRODUCT_TAG_TYPE } from '@src/quince-site-common/src/domains/ProductTag'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { PRODUCT_TAGS_IMAGE_LIMIT, TAG_LOCATION, TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { getDisplayKey } from '@src/utils/i18n.utils'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { ProductCardTag } from '../../product-card.types'

import { getTagPriority } from './product-tags.helper'
import styles from './product-tags.module.scss'
import { ReadyToShip } from './ready-to-ship'

// DS (Panda) twins of the PDP tag styles, used by the DS product gallery: the legacy SCSS
// rem values balloon 1.6x under the 16px DS root. Figma lays PDP tags out in a row with a
// space-8 gap, inset space-16 from the image edge.
const dsTagListCssRaw = css.raw({
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'row',
  gap: 'space-8',
  margin: 0,
  padding: 0,
  width: 'fit-content',
  maxWidth: '100%',
  overflow: 'hidden',
  position: 'absolute',
  zIndex: 10,
  left: 'space-16',
})

// Raw (not precompiled) so every `bottom` for a given state merges in ONE `css()` call —
// separate atomic classes setting `bottom` at the same breakpoint would leave the winner
// to Panda's extraction order (see ds-migration.md), unlike the source-ordered legacy SCSS.
const dsTagListTopCssRaw = css.raw({ top: 'space-16' })
const dsTagListBottomCssRaw = css.raw({ bottom: 'space-16' })

// Model-size tag pill sits just above the image's bottom edge (legacy `__modelSizeTag`).
const dsModelSizeTagListCssRaw = css.raw({ bottom: 'space-20' })

// QATC (quick-buy) insets its tags space-32 from the image edge and stacks them vertically, versus
// the PDP layout's space-16 row. Merged last so it overrides the shared list's top/left; bottom:auto
// pins it to a top-left inset regardless of the caller's position prop.
const dsTagListQatcCssRaw = css.raw({ flexDirection: 'column', top: 'space-32', bottom: 'auto', left: 'space-32' })

// Mobile PDP: product tags shift up to clear the model-size tag (legacy 4.8rem / 4.4rem).
const dsTagListAboveModelSizeCssRaw = css.raw({ lgDown: { bottom: 'space-48' } })
const dsTagListAboveModelSizeNewV2CssRaw = css.raw({ lgDown: { bottom: '44px' } })

// Mirrors the web-ui `Tag` recipe (`transparent` variant) per the Figma PDP tag spec;
// kept as an `li` style (not the `Tag` component) to preserve the ul>li tag-list markup.
const dsTagCss = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
  paddingBlock: 'space-4',
  paddingInline: 'space-8',
  backgroundColor: 'white-trans-70',
  backdropFilter: 'blur(2px)',
  color: 'black',
  textStyle: 'body-12',
})

// Legacy SECONDARY tags map to the web-ui `Tag` cantaloupe variant.
const dsTagSecondaryCss = css({ backgroundColor: 'cantaloupe-80' })

const dsTagRoundedCss = css({ borderRadius: 'radius-lg' })

// Legacy `.pdp__model__tag__modelSizeTag` pill.
const dsModelSizeTagCss = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  height: '30px',
  paddingBlock: 'space-8',
  paddingInline: 'space-12',
  borderRadius: 'radius-lg',
  background: 'white-trans-60',
  backdropFilter: 'blur(4px)',
  color: 'black',
  textAlign: 'center',
  fontFamily: 'Grosa, ui-sans-serif, system-ui, sans-serif',
  opacity: 1,
})

interface ProductTagsProps {
  applicableTags?: ProductCardTag[]
  readyToShip?: boolean
  className?: string
  position?: TAG_POSITION
  sticky?: boolean
  rounded?: boolean
  transformToSentenceCase?: boolean
  tagLocation?: TAG_LOCATION
  isModelSizeTag?: boolean
  productTagsNewClass?: boolean
  isModelSizeVisibleOnMobile?: boolean
}

export const ProductTags: FC<ProductTagsProps> = ({
  applicableTags = [],
  readyToShip = false,
  className,
  position = TAG_POSITION.BOTTOM,
  sticky,
  rounded,
  transformToSentenceCase = true,
  tagLocation,
  isModelSizeTag = false,
  productTagsNewClass,
  isModelSizeVisibleOnMobile,
}) => {
  const isNewLocalisedFlow = useI18nPreviewContext()
  const { isB2BMode } = useStorefrontModeContext()
  // Sort tags according to priority
  const sortedTags = useMemo(() => {
    return [...applicableTags].sort(
      (a, b) => getTagPriority(a.value.toLowerCase()) - getTagPriority(b.value.toLowerCase()),
    )
  }, [applicableTags])

  const isTagForProduct = tagLocation === TAG_LOCATION.PDP || tagLocation === TAG_LOCATION.QATC
  // PDP, QATC + model-size tags use the DS (Panda) tag styles under the gate; the product card
  // stays on SCSS. QATC (desktop quick-buy overlay) uses the DS tag styling (body-12 text) with its
  // own column + space-32 inset (dsTagListQatcCssRaw), so its modal needs no rem scale hack
  // (see product-buy-now-ds.styles.ts).
  const useDsTags =
    shouldUseDesignSystem() && (tagLocation === TAG_LOCATION.PDP || tagLocation === TAG_LOCATION.QATC || isModelSizeTag)

  const dsListClassName = classnames(
    css(
      dsTagListCssRaw,
      isModelSizeTag
        ? dsModelSizeTagListCssRaw
        : position === TAG_POSITION.TOP
          ? dsTagListTopCssRaw
          : dsTagListBottomCssRaw,
      isModelSizeVisibleOnMobile && dsTagListAboveModelSizeCssRaw,
      isModelSizeVisibleOnMobile && productTagsNewClass && dsTagListAboveModelSizeNewV2CssRaw,
      tagLocation === TAG_LOCATION.QATC && dsTagListQatcCssRaw,
    ),
    className,
  )

  const legacyListClassName = classnames(
    styles.tagList,
    styles[`tagList__${position}`],
    className,
    sticky && styles[`tagList__stickTo__${position}`],
    {
      [`${styles.tagList__buyNow}`]: tagLocation === TAG_LOCATION.QATC,
      [`${styles.tagList__pdp}`]: tagLocation === TAG_LOCATION.PDP,
      [`${styles.tagList__productCard}`]: tagLocation === TAG_LOCATION.PRODUCT_CARD,
      [`${styles.tagList__modelSizeTag}`]: isModelSizeTag,
      [`${styles.tagList__modelSizeVisibleOnMobile}`]: isModelSizeVisibleOnMobile,
      [`${styles.tagList__modelSizeVisibleOnMobile__newDesignV2}`]: isModelSizeVisibleOnMobile && productTagsNewClass,
    },
  )

  return (
    <ul className={useDsTags ? dsListClassName : legacyListClassName}>
      {sortedTags.slice(0, PRODUCT_TAGS_IMAGE_LIMIT).map(tag => {
        const tagText = getDisplayKey(tag, 'displayName', tag.value, isNewLocalisedFlow || isB2BMode)
        return (
          <li
            className={
              useDsTags
                ? classnames(
                    dsTagCss,
                    tag.tagType === PRODUCT_TAG_TYPE.SECONDARY && dsTagSecondaryCss,
                    rounded && dsTagRoundedCss,
                    isModelSizeTag && dsModelSizeTagCss,
                  )
                : classnames(
                    styles.tag,
                    tag.tagType && styles[`tag__${tag.tagType}`],
                    rounded && styles.tag__rounded,
                    isTagForProduct && styles.pdp_tag,
                    isModelSizeTag && styles.pdp__model__tag__modelSizeTag,
                  )
            }
            key={tag.value}
          >
            {transformToSentenceCase ? productHelper.getTagValueWithFirstLetterUpperCase(tagText) : tagText}
          </li>
        )
      })}
      <ReadyToShip shouldShowReadyToShipTag={readyToShip} />
    </ul>
  )
}
